import { AxiosError } from 'axios'

import {
  ASNUnit,
  Case,
  Container,
  Generic,
  PaginatedResponse,
  Pallet,
  Place,
  Product,
  UnitOfMeasure,
} from '../../api/models'
import { skuit } from '../../services/skuit'
import { Action, ApplyScan, Mode } from './interface'
import toast from 'react-hot-toast'
import { Handler, HandlerInput } from './base'

interface PalletCreatePayload {
  count: number
  batch_type: string // eslint-disable-line camelcase
  client_uid: string  // eslint-disable-line camelcase
  expires_on: string // eslint-disable-line camelcase
}

export enum ReceivingEvent {
  Loaded,
  Scan,
  Mode,
  NumPallets,
  Save,
  CaseSelected,
  SkuSelected,
  MeasureProvided,
}

export type ReceivingHandlerCallback = (data: any) => void

interface ASNUnitCreate {
  asn: string
  item: string
  sku: string
  location: string
  count: number
}

const getKey = (product: Generic, sku?: Generic, productCase?: Case) => {
  return `${product.object.guid}-${sku?.object?.guid}-${productCase?.guid}`
}

class ReceiveItem {
  product: Generic
  sku?: Generic
  skus?: Generic[]
  productCase?: Case
  count: number

  constructor(
    product: Generic,
    sku?: Generic,
    skus?: Generic[],
    productCase?: Case,
    count: number = 0
  ) {
    this.product = product
    this.sku = sku
    this.skus = skus
    this.productCase = productCase
    this.count = count
    if (!this.sku && skus?.length === 1) {
      this.sku = skus[0]
    }
  }

  get key() {
    return getKey(this.product, this.sku, this.productCase)
  }
}

interface ReceivingHandlerInput extends HandlerInput {
  asn: Generic
}

export class ReceivingHandler extends Handler {
  asn: Generic
  to?: Generic
  mode: Mode
  asnUnits?: PaginatedResponse<ASNUnit>
  loadingUnits?: boolean
  asnPallets?: PaginatedResponse<Pallet>
  loadingPallets?: boolean
  receiving: [Generic, ReceiveItem[]][]
  receivingTo?: [Generic, ReceiveItem[]]
  callbacks: ReceivingHandlerCallback[]
  lastCaseMap: { [product: string]: Case | undefined }
  lastSkuMap: { [product: string]: Generic | undefined }
  toMeasure?: ReceiveItem
  toResolve?: ReceiveItem

  constructor(props: ReceivingHandlerInput) {
    super(props)
    this.asn = props.asn
    this.mode = Mode.Scan
    this.receiving = []
    this.callbacks = []
    this.lastCaseMap = {}
    this.lastSkuMap = {}
    this.fetchUnits()
    this.fetchPallets()
  }

  apply = ({ scanned, context, local }: ApplyScan): [Action, any | undefined] => {
    switch (scanned.objectType) {
      case Place.type:
      case Container.type:
      case Pallet.type: {
        this.to = scanned
        let entry: [Generic, ReceiveItem[]] | undefined
        const index = this.receiving
          .map(([to]: [Generic, ReceiveItem[]]) => to.object.guid)
          .indexOf(this.to?.object.guid)
        if (index > -1) {
          entry = this.receiving[index]
          this.receiving.splice(index, 1)
        }
        if (entry) {
          this.receiving.splice(0, 0, entry)
        }
        this.receivingTo = entry
        this.lastSkuMap = {}
        this.notify({ event: ReceivingEvent.Scan })
        break
      }
      case Case.type:
      case Product.type: {
        if (this.toResolve) {
          toast.error('Must first select the SKU')
          return [Action.None, undefined]
        }
        if (!this.receivingTo) {
          this.receivingTo = [this.to as Generic, []]
          this.receiving.splice(0, 0, this.receivingTo)
        }
        const [, items] = this.receivingTo as [Generic, ReceiveItem[]]
        const product = context?.product || scanned
        const sku =
          this.lastSkuMap[product.object.guid] ||
          (context?.skus?.length === 1 ? context.skus[0] : context?.sku)
        const key = getKey(
          product,
          sku,
          scanned.objectType === Case.type ? scanned.object : this.lastCaseMap[product.object.guid]
        )
        const index = items.map((g: ReceiveItem) => g.key).indexOf(key)
        let item: ReceiveItem
        if (index > -1) {
          item = items[index]
          items.splice(index, 1)
        } else {
          item = new ReceiveItem(
            context?.product || scanned,
            sku,
            context?.skus,
            scanned.objectType === Case.type ? scanned.object : undefined
          )
          if (!item.sku && item.skus && item.skus.length > 1) {
            this.toResolve = item
          }
        }
        if (sku?.object.unitOfMeasure === UnitOfMeasure.lbs) {
          if (local?.quantity) {
            if (this.mode === Mode.Scan) {
              item.count += local.quantity
            } else {
              item.count -= local.quantity
            }
            if (item.count !== 0) {
              items.splice(0, 0, item)
            }
          } else {
            this.toMeasure = item
          }
        } else {
          if (this.mode === Mode.Scan) {
            item.count += 1
          } else {
            item.count -= 1
          }
          if (item.count !== 0) {
            items.splice(0, 0, item)
          }
        }
        this.notify({ event: ReceivingEvent.Scan })
      }
    }
    return [Action.None, undefined]
  }

  caseSelector = (items: ReceiveItem[], item: ReceiveItem) => {
    return (from: Product | Case, to: Product | Case) => {
      if (this.mode === Mode.Scan) {
        item.count -= 1
      } else {
        item.count += 1
      }
      if (item.count === 0) {
        const index = items.map((g: ReceiveItem) => g.key).indexOf(item.key)
        items.splice(index, 1)
      }
      const productCase = to.type === Case.type ? to as Case : undefined
      const key = getKey(item.product, item.sku, productCase)
      const newIndex = items.map((g: ReceiveItem) => g.key).indexOf(key)
      let newItem: ReceiveItem
      if (newIndex > -1) {
        newItem = items[newIndex]
      } else {
        newItem = new ReceiveItem(item.product, item.sku, item.skus, productCase)
      }
      if (this.mode === Mode.Scan) {
        newItem.count += 1
      } else {
        newItem.count -= 1
      }
      if (newIndex === -1 && newItem.count !== 0) {
        items.splice(0, 0, newItem)
      }
      this.lastCaseMap[item.product.object.guid] = productCase
      this.notify({ event: ReceivingEvent.CaseSelected })
    }
  }

  skuSelector = (items: ReceiveItem[], item: ReceiveItem) => {
    return (sku: Generic) => {
      const index = items.indexOf(item)
      items.splice(index, 1)
      this.toResolve = undefined
      const context = {
        type: 'context',
        sku,
        skus: item.skus,
        case: item.productCase,
      }
      this.apply({
        scanned: item.product,
        context,
      })
      this.lastSkuMap[item.product.object.guid] = sku
    }
  }

  provideMeasure = (items: ReceiveItem[], item: ReceiveItem) => {
    return (measure: number) => {
      if (this.mode === Mode.Scan) {
        item.count += measure
      } else {
        item.count -= measure
      }
      if (item.count !== 0) {
        items.splice(0, 0, item)
      }
      this.toMeasure = undefined
      this.notify({ event: ReceivingEvent.MeasureProvided })
    }
  }

  fetchUnits = () => {
    this.loadingUnits = true
    skuit.sc.asn.units
      .list({ params: { asn: this.asn.object.guid } })
      .then((data: PaginatedResponse<ASNUnit>) => {
        this.asnUnits = data
        this.loadingUnits = false

        this.notify({ event: ReceivingEvent.Loaded })
      })
      .catch((error: AxiosError) => {
        toast.error(`Failed to fetch ASN Units. "${error.response?.data}"`)
      })
  }

  fetchPallets = () => {
    this.loadingPallets = true
    skuit.sc.pallets
      .list({ params: { asns: this.asn.object.guid } })
      .then((data: PaginatedResponse<Pallet> | undefined) => {
        this.asnPallets = data
        this.loadingPallets = false

        this.notify({ event: ReceivingEvent.Loaded })
      })
      .catch((error: AxiosError) => {
        toast.error(`Failed to fetch ASN Pallets. "${error.response?.data}"`)
      })
  }

  setMode = (mode: Mode) => {
    this.mode = mode
    this.notify({ event: ReceivingEvent.Mode })
  }

  getContext = () => {
    return { activity: 'receiving', asn: this.asn.object.guid }
  }

  createPallets = (palletData: PalletCreatePayload) => {
    this.notify({ event: ReceivingEvent.Save, saving: true })
    const data: any = {
      client: this.asn?.object.client.guid,
      asn: this.asn?.object.guid,
      place: this.to?.object.guid,
      count: palletData.count,
    }
    if (palletData.batch_type && palletData.client_uid) {
      data.batch = {
        batch_type: palletData.batch_type,
        client_uid: palletData.client_uid,
        expires_on: palletData.expires_on,
      }
    }
    skuit.sc.pallets
      .create(data)
      .then(() => {
        this.fetchPallets()
        toast.success(`Created ${palletData.count} pallets.`)
      })
      .catch((error: AxiosError) => {
        toast.error(`Failed to add pallets. "${JSON.stringify(error?.response?.data || '')}"`)
      })
      .finally(() => {
        this.notify({ event: ReceivingEvent.Save, saving: false })
      })
  }

  save = () => {
    let unitPayloads: ASNUnitCreate[] = []
    this.receiving.map(([location, items]) => {
      const placeScans = items.map((item) => {
        return {
          asn: this.asn.object.guid,
          item: item.productCase ? item.productCase.guid : item.product.object.guid,
          sku: item.sku?.object.guid,
          location: location.object.guid,
          count: item.count,
        }
      })

      unitPayloads = unitPayloads.concat(placeScans)
      return unitPayloads
    })

    if (unitPayloads.length) {
      const data = { scans: unitPayloads }
      this.notify({ event: ReceivingEvent.Save, saving: true })
      skuit.sc.asn.units
        .create(data)
        .then(() => {
          this.receiving = []
          this.receivingTo = undefined
          this.fetchUnits()
          toast.success(`Performed ${unitPayloads.length} receiving scans.`)
        })
        .catch((error: AxiosError) => {
          toast.error(`Failed to add pallets. "${JSON.stringify(error?.response?.data || '')}"`)
        })
        .finally(() => {
          this.notify({ event: ReceivingEvent.Save, saving: false })
        })
    }
  }
}
